import "./styles.less";

import { MouseEvent, useContext, useEffect, useRef, useState } from "react";
import { OwnerWspInterface, useWhatsppButton } from "./hook";
import { ModalWpp } from "../../../ModalWpp/web";
import { WhatsAppOutlined } from "@ant-design/icons";
import { useFacebookPixel } from "../../../../../GlobalHooks/web/FacebookPixel.hook";
import { ModalState } from "../../TextForm/type";
import { LoadingIcon } from "shared-components/Components/CustomIcons/LoadingIcon/LoadingIcon";
import { useUser } from "shared-components/Components/User/User.hook";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useRouter } from "next/router";
import { CountryCode, getCountryCallingCode, parsePhoneNumberWithError } from "libphonenumber-js";
import { useContactModal } from "shared-components/Contexts/ContactModal/context";
import { getInitialContactForm } from "src/utils/Functions";

export const WhatsappButton = ({
	id,
	disabled = false,
	queryRouter,
	onWpClick,
	forceState = ModalState.DEFAULT,
	owner,
	isProject = false,
	hasAcceptedTerms = true,
	onTermsError,
	callback,
	handleCloseContactModal,
	source,
	propertyLink
}: OwnerWspInterface) => {
	const { main_domain, country_code } = useContext(ConfigStateContext);
	const { ctaButtons } = useContactModal();
	const {
		isLoggedIn,
		user: { data: dataUser },
	} = useUser();
	const FP = useFacebookPixel();
	const callWpp = useRef(null);
	const [modal, setModal] = useState(false);
	const screens = useBreakpoint();
	const router = useRouter();
	const contactForm = getInitialContactForm(isLoggedIn, dataUser);

	const isPrimaryButton = ctaButtons?.whatsapp && !ctaButtons?.contact && !ctaButtons?.phone && source === "contactModal";

	if (!owner) return null;

	const [selectedCountryCode, setSelectedCountryCode] = useState({
		code: "",
		value: "",
	});

	const { viewWpp, loading, linkWpp, wppPhone, userExistsQuery } = useWhatsppButton(
		id,
		isProject,
		{
			email: contactForm?.email,
			phone: selectedCountryCode.value + contactForm?.phone,
			name: contactForm?.name,
		}
	);

	useEffect(() => {
		if (country_code) {
		  	setSelectedCountryCode({
				code: country_code as CountryCode,
				value: `+${getCountryCallingCode(country_code as CountryCode)}`,
			})
		}
	}, [country_code]);

	// Simula el click de ver "Whatsapp"
	useEffect(() => {
		setTimeout(() => {
			if (queryRouter && queryRouter === "wpp") {
				if (callWpp.current) callWpp.current.click();
			}
		}, 3000);
	}, [queryRouter]);

	useEffect(() => {
		if (forceState == ModalState.CLOSE) {
			setModal(false);
		} else if (forceState == ModalState.OPEN) {
			setModal(true);
		}
	}, [forceState]);

	const sendEvent = () => {
		const categoryName: string = "Consultas Wpp";
		FP.StandardEvent(categoryName);
	};

	const inmoLink = main_domain + (propertyLink || router.asPath);

	const processPhoneNumber = (phone: string) => {
		try {
			const parsedNumber = parsePhoneNumberWithError(phone);
			return `+${parsedNumber.countryCallingCode}${parsedNumber.nationalNumber}`;
		} catch (error) {
			try {
				const parsedWithDefault = parsePhoneNumberWithError(
					phone,
					country_code as CountryCode
				);

				return `+${parsedWithDefault.countryCallingCode}${parsedWithDefault.nationalNumber}`;
			} catch (e) {
				const parsedWithDefault = parsePhoneNumberWithError(
					phone,
					selectedCountryCode.code as CountryCode
				);
				return `+${parsedWithDefault.countryCallingCode}${parsedWithDefault.nationalNumber}`;
			}
		}
	};

	const openWhatsapp = async (data: { phone: string; __typename: string }[]) => {
		if (data) {
			const phones = data.filter(e => e.phone && e.__typename === "Phone");
			if (phones && phones[0]) {
				const phoneData = phones[0].phone.replace(/\s+/g, '');
				const cleanPhone = await processPhoneNumber(phoneData)?.replace('+', '');
				const encodedText = encodeURIComponent(`Estoy interesado en su inmueble ${id} que vi por ${inmoLink}`);
				const link = `https://wa.me/${cleanPhone}?text=${encodedText}&app_absent=1`;
				if (screens.xl || screens.lg) {
					window.open(link, "_blank");
				} else {
					window?.location?.replace(link);
				}

				return true;
			}
		}
		return false;
	};

	const triggerWhatsappFlow = (agentData?: any) => {
		sendEvent();
		setModal(true);
		if (agentData) {
			openWhatsapp(agentData);
		}
	};

	const handleClick = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
	
		const shouldTriggerCallback = callback && !isLoggedIn;
		const shouldCheckUserExists = contactForm?.email;
	
		if (country_code !== "CO") {
			if (owner?.has_whatsapp) {
				viewWpp().then(data => {
					if (data) {
						triggerWhatsappFlow(data?.data?.requestPhoneAgent);
					}
				});
			} else {
				triggerWhatsappFlow();
			}
			return;
		}
	
		if (shouldTriggerCallback) {
			callback({ctaButtons: {
				contact: false,
				phone: false,
				whatsapp: true
			}});
			return;
		}
	
		if (shouldCheckUserExists) {
			userExistsQuery({ variables: { email: contactForm.email } });
		}
	
		if (owner?.has_whatsapp) {
			viewWpp().then(data => {
				if (data) {
					triggerWhatsappFlow(data?.data?.requestPhoneAgent);
				}
			});
		} else {
			triggerWhatsappFlow();
		}
	};

	return (
		<>
			<button
				ref={callWpp}
				className={isPrimaryButton ? "btn-primary btn-modal" : "wpp-button"}
				disabled={disabled}
				onClick={(e: any) => {
					if (hasAcceptedTerms) {
							handleClick(e);
					} else {
						if (onTermsError) onTermsError(e);
					}
				}}
			>
				{loading ?
					<LoadingIcon classes="loading-icon" />
				: <span className="btn-icon"><WhatsAppOutlined style={{ color: "currentColor" }} /></span>}
				<span className="btn-text">Whatsapp</span>
			</button>

			<ModalWpp
				visible={modal}
				link={linkWpp}
				property_id={id}
				phone={wppPhone}
				loading={loading}
				onWpClick={onWpClick}
				owner_name={owner?.name}
				onCancel={() => {
					setModal(false)
					if (handleCloseContactModal) handleCloseContactModal();
				}}
			/>
		</>
	);
};



